/**
 * KONFIGURACJA ATMOSFERY ŚWIETLNEJ — camera-fixed light overlay per-biom.
 *
 * W odróżnieniu od `ambientFx.config.ts` (world-positioned particles —
 * śnieg/piasek przewijające się z kamerą), atmosfera to nakładka "przyklejona"
 * do viewportu (`scrollFactor=0`), która kadruje i barwi cały ekran. Daje
 * tani, ale mocny efekt "żywej planszy" — zero nowych PNG, tekstury generowane
 * proceduralnie w `PreloadScene` (`fx_vignette`, `fx_sunbeam`).
 *
 * Trzy niezależne, opcjonalne warstwy (każda może być `undefined`):
 *  - **vignette** — przyciemnienie/zabarwienie rogów ekranu (kinowy kadr).
 *  - **rays** — N miękkich snopów światła (god rays) sączących się ukośnie,
 *    lekko dryfujących + pulsujących. Pomijane w pixel-art biomie.
 *  - **tint** — pełnoekranowa fala koloru, bardzo subtelna, "oddech" nastroju.
 *
 * **Pulsacja:** każda warstwa ma własną sin-oscylację (Hz). Podana alpha to
 * SZCZYT (`alphaPeak`) — puls schodzi w dół do `alphaPeak*(1-pulseDepth)`.
 * `pulseDepth` = amplituda jako ułamek (0 = brak pulsu, statyczna warstwa).
 *
 * **Tint vs barwa tekstury:** tekstury są bake'owane na BIAŁO z gradientem
 * alphy; `color` aplikujemy runtime przez `setTint` (white × tint = tint).
 * Dzięki temu jedna tekstura obsługuje wszystkie biomy.
 *
 * **Depth:** overlay rysowany wysoko (nad światłem gry), ale i tak jest pod
 * React HUD (osobna warstwa DOM nad canvasem). Patrz `AtmosphereSystem`.
 *
 * **Performance:** garstka statycznych Image/TileSprite + kilka tweenów alphy.
 * Pomijalny koszt względem particle systemu.
 */

import type { AtmosphereKey } from '@/domain/types/atmosphere';
export type { AtmosphereKey };

/** Blend mode jako string — konwertowane na `Phaser.BlendModes` w systemie. */
export type AtmosphereBlendMode = 'NORMAL' | 'ADD' | 'SCREEN' | 'MULTIPLY';

/** Róg/krawędź viewportu, od której biją promienie słońca. */
export type RayOrigin = 'top-left' | 'top' | 'top-right';

/** Winieta — przyciemnienie/zabarwienie rogów (transparentny środek). */
export interface VignetteConfig {
  /** Kolor rogów (tint na białą teksturę gradientu). Ciemny dla klasycznej winiety. */
  color: number;
  /** Szczytowa alpha rogów (po pulsie). 0.25–0.5 = subtelnie, >0.6 = ciężki kadr. */
  alphaPeak: number;
  /** Częstotliwość oddechu winiety (Hz). 0 = statyczna. */
  pulseHz: number;
  /** Amplituda pulsu jako ułamek alphaPeak (0–1). 0.1 = ledwo widoczny oddech. */
  pulseDepth: number;
}

/** Promienie słońca — N miękkich snopów światła. */
export interface RaysConfig {
  /** Kolor snopów (tint na białą beam-teksturę). Ciepły = słońce, zimny = księżyc. */
  color: number;
  /** Blend — ADD/SCREEN dają świetlisty glow; NORMAL byłby matowy. */
  blendMode: AtmosphereBlendMode;
  /** Liczba snopów. 2–4 = naturalna wiązka, >5 zaczyna zasłaniać. */
  count: number;
  /** Szczytowa alpha pojedynczego snopu. Niska (0.1–0.25) — światło ma być subtelne. */
  alphaPeak: number;
  /** Częstotliwość pulsu jasności (Hz). Snopy desync'owane fazą per-instancja. */
  pulseHz: number;
  /** Amplituda pulsu jako ułamek alphaPeak (0–1). */
  pulseDepth: number;
  /** Od którego rogu biją (kierunek nachylenia wiązki). */
  origin: RayOrigin;
  /** Bazowe nachylenie snopu od pionu (stopnie). Per-snop losowo ±rozrzut. */
  tiltDeg: number;
  /** Amplituda powolnego dryfu nachylenia (stopnie) — światło "kołysze się". */
  swayDeg: number;
  /** Częstotliwość dryfu nachylenia (Hz). Wolna (0.02–0.06) = leniwy ruch. */
  swayHz: number;
}

/** Pełnoekranowa fala koloru — "oddech" nastroju biomu. */
export interface TintConfig {
  /** Kolor wylewki. */
  color: number;
  /** Blend — ADD rozjaśnia (ciepły flush), MULTIPLY przyciemnia/barwi. */
  blendMode: AtmosphereBlendMode;
  /**
   * Szczytowa alpha wylewki (po pulsie) — jak `vignette.alphaPeak`/`rays.alphaPeak`.
   * Puls schodzi W DÓŁ od tej wartości do `alphaPeak*(1-pulseDepth)`, NIE oscyluje
   * wokół niej (patrz helper `pulse()` w AtmosphereSystem). Bardzo niska — 0.04–0.12.
   */
  alphaPeak: number;
  /** Częstotliwość oddechu (Hz). */
  pulseHz: number;
  /** Amplituda pulsu jako ułamek alphaPeak (0–1). */
  pulseDepth: number;
}

/** Pełen preset atmosfery — wszystkie warstwy opcjonalne. */
export interface AtmospherePreset {
  vignette?: VignetteConfig;
  rays?: RaysConfig;
  tint?: TintConfig;
}

// ─────────────────────────────────────────────────────────────────────────────

export const ATMOSPHERE_PRESETS: Record<AtmosphereKey, AtmospherePreset> = {
  // Bright autumn afternoon: amber light and soft plum edges.
  halloween_harvest: {
    vignette: { color: 0x76567f, alphaPeak: 0.1, pulseHz: 0.035, pulseDepth: 0.08 },
    rays: {
      color: 0xffe1a1, blendMode: 'ADD', count: 2, alphaPeak: 0.04,
      pulseHz: 0.07, pulseDepth: 0.35, origin: 'top-left', tiltDeg: 24,
      swayDeg: 3, swayHz: 0.025,
    },
    tint: { color: 0xffd7a3, blendMode: 'ADD', alphaPeak: 0.012, pulseHz: 0.045, pulseDepth: 0.2 },
  },
  // ─── MEADOW — słoneczny dzień ───────────────────────────────────────────────
  // Ciepłe złote promienie z góry-lewej, miękka ciepła winieta, leniwy złoty
  // oddech. Klimat: pogodne południe na łące.
  meadow_sun: {
    vignette: {
      color: 0x2e5a2e,        // ciemna zieleń — winieta wtapia się w trawę
      alphaPeak: 0.32,
      pulseHz: 0.05,
      pulseDepth: 0.15,
    },
    rays: {
      color: 0xfff3c4,        // ciepłe słoneczne złoto
      blendMode: 'ADD',
      count: 3,
      alphaPeak: 0.16,
      pulseHz: 0.12,
      pulseDepth: 0.45,
      origin: 'top-left',
      tiltDeg: 22,
      swayDeg: 4,
      swayHz: 0.04,
    },
    tint: {
      color: 0xffe9a8,        // ciepły złoty flush
      blendMode: 'ADD',
      alphaPeak: 0.05,
      pulseHz: 0.06,
      pulseDepth: 0.5,
    },
  },

  // ─── MOUNTAIN — mroźny szczyt ───────────────────────────────────────────────
  // Chłodne biało-niebieskie światło, zimna winieta, lodowaty oddech. Promienie
  // bledsze i bardziej pionowe (wysokie słońce nad śniegiem).
  mountain_cold: {
    vignette: {
      color: 0x2a3a52,        // chłodny granat — mroźny kadr
      alphaPeak: 0.38,
      pulseHz: 0.04,
      pulseDepth: 0.12,
    },
    rays: {
      color: 0xdfeeff,        // zimne biało-niebieskie światło
      blendMode: 'SCREEN',
      count: 2,
      alphaPeak: 0.12,
      pulseHz: 0.1,
      pulseDepth: 0.4,
      origin: 'top',
      tiltDeg: 10,
      swayDeg: 3,
      swayHz: 0.035,
    },
    tint: {
      color: 0xbcd4f0,        // lekka mroźna mgiełka
      blendMode: 'SCREEN',
      alphaPeak: 0.06,
      pulseHz: 0.05,
      pulseDepth: 0.45,
    },
  },

  // ─── STAR WARS — pustynny żar (bliźniacze słońca) ──────────────────────────
  // Mocne pomarańczowe promienie z dwóch stron (top-right dominujący), gorąca
  // winieta, intensywniejszy oddech. Klimat: skwar Tatooine.
  desert_heat: {
    vignette: {
      color: 0x5a3318,        // przypalony brąz — gorący kadr
      alphaPeak: 0.34,
      pulseHz: 0.06,
      pulseDepth: 0.18,
    },
    rays: {
      color: 0xffd08a,        // pomarańczowo-bursztynowe słońce
      blendMode: 'ADD',
      count: 4,
      alphaPeak: 0.2,
      pulseHz: 0.14,
      pulseDepth: 0.5,
      origin: 'top-right',
      tiltDeg: 26,
      swayDeg: 5,
      swayHz: 0.05,
    },
    tint: {
      color: 0xffcaa0,        // gorący bursztynowy flush
      blendMode: 'ADD',
      alphaPeak: 0.08,
      pulseHz: 0.07,
      pulseDepth: 0.55,
    },
  },

  // ─── PIXEL WORLD — retro CRT ────────────────────────────────────────────────
  // Bez god rays (snopy światła kłócą się z pixel-artem). Mocniejsza winieta
  // imitująca zaokrąglony ekran kineskopu + lekki neonowy puls koloru.
  pixel_crt: {
    vignette: {
      color: 0x0a0a18,        // głęboki granat — ramka kineskopu
      alphaPeak: 0.45,
      pulseHz: 0.03,
      pulseDepth: 0.1,
    },
    tint: {
      color: 0x4be0ff,        // neonowy cyjan — retro poświata
      blendMode: 'ADD',
      alphaPeak: 0.05,
      pulseHz: 0.09,
      pulseDepth: 0.6,
    },
    // rays: brak — pixel-art zostaje czysty.
  },

  // ─── OCEAN — głębiny morskie ────────────────────────────────────────────────
  // Ciemna granatowa winieta (jak pod wodą, im dalej od środka tym głębiej),
  // chłodne cyjanowo-białe snopy „kaustyk" sączące się z góry (słońce przez taflę)
  // i delikatny akwamarynowy oddech. Klimat: spokojna, błękitna toń.
  ocean_depths: {
    vignette: {
      color: 0x07294a,        // głęboki granat — toń ciemnieje ku rogom
      alphaPeak: 0.44,
      pulseHz: 0.04,
      pulseDepth: 0.14,
    },
    rays: {
      color: 0xbfeeff,        // cyjanowo-białe światło przez wodę (kaustyki)
      blendMode: 'SCREEN',
      count: 3,
      alphaPeak: 0.16,
      pulseHz: 0.1,
      pulseDepth: 0.5,
      origin: 'top',
      tiltDeg: 14,
      swayDeg: 5,
      swayHz: 0.05,
    },
    tint: {
      color: 0x2fb6d6,        // akwamarynowy flush
      blendMode: 'SCREEN',
      alphaPeak: 0.08,
      pulseHz: 0.05,
      pulseDepth: 0.5,
    },
  },

  // ─── FOOD — przytulny bar / jasna kuchnia ───────────────────────────────────
  // Ciepła miodowo-bursztynowa winieta (jak wnętrze fast foodu), miękkie ciepłe
  // snopy światła z lampy nad blatem i apetyczny złoto-pomarańczowy oddech.
  // Klimat: jasna, wesoła, „smakowita" restauracja.
  food_diner: {
    vignette: {
      color: 0x5e3a1a,        // ciepły przypieczony brąz — kadr wnętrza baru
      alphaPeak: 0.30,
      pulseHz: 0.05,
      pulseDepth: 0.16,
    },
    rays: {
      color: 0xffe0a8,        // ciepłe światło lampy / złote frytkowe
      blendMode: 'ADD',
      count: 3,
      alphaPeak: 0.17,
      pulseHz: 0.12,
      pulseDepth: 0.45,
      origin: 'top',
      tiltDeg: 16,
      swayDeg: 4,
      swayHz: 0.045,
    },
    tint: {
      color: 0xffcf8c,        // apetyczny złoto-pomarańczowy flush
      blendMode: 'ADD',
      alphaPeak: 0.06,
      pulseHz: 0.06,
      pulseDepth: 0.5,
    },
  },

  // ── ATLANTIS (biom 7) — turkusowa winieta głębin + złote boskie god-rays z góry ──
  atlantis_depths: {
    vignette: {
      color: 0x0c3a44,        // głęboki turkusowo-granatowy kadr głębin
      alphaPeak: 0.34,
      pulseHz: 0.05,
      pulseDepth: 0.16,
    },
    rays: {
      color: 0xffe6a8,        // ciepłe złote boskie światło z góry
      blendMode: 'ADD',
      count: 4,
      alphaPeak: 0.16,
      pulseHz: 0.1,
      pulseDepth: 0.45,
      origin: 'top',
      tiltDeg: 12,
      swayDeg: 5,
      swayHz: 0.04,
    },
    tint: {
      color: 0x2fd0c8,        // turkusowo-złoty flush
      blendMode: 'ADD',
      alphaPeak: 0.06,
      pulseHz: 0.06,
      pulseDepth: 0.5,
    },
  },

  // ─── BEACH (event Wakacje Capibar) — ciepłe wakacyjne słońce ────────────────
  // Wzorzec meadow_sun, ale winieta w ciepłym piaskowym brązie (nie zieleń) i
  // mocniejszy złoty flush — słoneczna, wesoła plaża.
  beach_sun: {
    vignette: {
      color: 0x9a6b34,        // ciepły piaskowy brąz — kadr plaży
      alphaPeak: 0.24,
      pulseHz: 0.05,
      pulseDepth: 0.14,
    },
    rays: {
      color: 0xfff0c0,        // jasne letnie słońce
      blendMode: 'ADD',
      count: 3,
      alphaPeak: 0.18,
      pulseHz: 0.1,
      pulseDepth: 0.45,
      origin: 'top-left',
      tiltDeg: 24,
      swayDeg: 4,
      swayHz: 0.04,
    },
    tint: {
      color: 0xffe6a0,        // ciepły złoty blask słońca
      blendMode: 'ADD',
      alphaPeak: 0.07,
      pulseHz: 0.06,
      pulseDepth: 0.5,
    },
  },

  // ─── SCHOOL (biom 8) — poranek pierwszego dnia szkoły ──────────────────────
  // Miodowe światło przez wysokie okna klasy. Winieta CELOWO słabsza niż
  // w innych biomach (0.26) — to ma być jasny, optymistyczny świat, nie
  // mroczna sala. Snopy z góry, lekko przechylone jak przez ramy okien.
  school_morning: {
    vignette: {
      color: 0x6b5330,        // ciepły brąz sali (drewno ławek), nie szarość
      alphaPeak: 0.26,
      pulseHz: 0.04,
      pulseDepth: 0.12,
    },
    rays: {
      color: 0xfff0c0,        // miodowe poranne słońce
      blendMode: 'ADD',
      count: 4,
      alphaPeak: 0.18,
      pulseHz: 0.09,
      pulseDepth: 0.4,
      origin: 'top',
      tiltDeg: 16,
      swayDeg: 4,
      swayHz: 0.03,
    },
    tint: {
      color: 0xffd98a,        // ciepły kremowy flush papieru
      blendMode: 'ADD',
      alphaPeak: 0.05,
      pulseHz: 0.05,
      pulseDepth: 0.45,
    },
  },

  // ─── ARCTIC (biom 9) — jasny polarny zmierzch pod zorzą ──────────────────
  // Chłodny kadr bez „nocnego filtra”: biała kra i czarne kontury assetów
  // muszą pozostać czytelne na telefonie. Promienie imitują miękkie kurtyny zorzy.
  arctic_twilight: {
    vignette: {
      color: 0x244b68,
      alphaPeak: 0.3,
      pulseHz: 0.035,
      pulseDepth: 0.16,
    },
    rays: {
      color: 0xa8ffe4,
      blendMode: 'SCREEN',
      count: 4,
      alphaPeak: 0.15,
      pulseHz: 0.075,
      pulseDepth: 0.5,
      origin: 'top-right',
      tiltDeg: -14,
      swayDeg: 7,
      swayHz: 0.025,
    },
    tint: {
      color: 0x927de3,
      blendMode: 'ADD',
      alphaPeak: 0.045,
      pulseHz: 0.045,
      pulseDepth: 0.55,
    },
  },
  // ─── FOREST SHADE (Puszcza Niepołomicka) — chłodny cień koron, ciepłe przebłyski ─
  // Pod gęstymi dębami jest CIEMNIEJ i zieleniej niż na łące, ale przez dziury
  // w koronach przebijają wąskie, ciepłe smugi. Winieta mocniejsza niż w innych
  // biomach (uczucie „jestem w środku lasu"), promienie wąskie i nieliczne.
  forest_shade: {
    vignette: {
      color: 0x1e3a1c,          // ciemna zieleń poszycia, nie czerń
      alphaPeak: 0.34,
      pulseHz: 0.03,
      pulseDepth: 0.14,
    },
    rays: {
      color: 0xfff3b8,          // miodowe słońce przez dziurę w koronie
      blendMode: 'ADD',
      count: 3,
      alphaPeak: 0.2,
      pulseHz: 0.07,
      pulseDepth: 0.45,
      origin: 'top-left',
      tiltDeg: 12,
      swayDeg: 5,               // smuga drga jak przy poruszanych gałęziach
      swayHz: 0.035,
    },
    tint: {
      color: 0x8fd17a,          // delikatny zielony refleks od liści
      blendMode: 'ADD',
      alphaPeak: 0.05,
      pulseHz: 0.04,
      pulseDepth: 0.4,
    },
  },
};
